/**
 * The privacy policy, as a page of the application rather than a file on a website.
 *
 * It is here because this is the only place the people it describes can be reached: the
 * archive has no public pages. Everybody who reads it has either been given a seat in
 * Cloudflare Access or an account on the donor door, and both of those arrive by
 * invitation — so a policy hosted on the open web would be read by nobody it is about,
 * while the account menu is one click from every page either of them can open.
 *
 * The text is written out here rather than fetched, for two reasons. It is the same for
 * every reader, so there is nothing for the server to decide; and a policy that renders
 * from a request can fail to render, which is the one thing a legal notice may not do.
 * Changing it is a deploy, which is also the record of when it changed.
 *
 * What it says is meant to match what the code does, and the specifics are deliberate —
 * PBKDF2 rounds, the two session windows, which columns hold an IP address. If any of
 * those change, this page is part of the change: see LAST_UPDATED below.
 */
import { Card } from '../components/ui'
import { PageHeader } from '../App'

/**
 * Shown under the title, and the thing a reader checks first. Written out as text rather
 * than derived from a build date, because it is the date the *policy* changed and not
 * the date this file was last touched — a typo fix is not a new policy.
 */
const LAST_UPDATED = '22 August 2026'

const CONTACT = 'info@silkroaders.org'

/** A mail link, since every route out of this page is the same address. */
const Mail = () => <a href={`mailto:${CONTACT}`}>{CONTACT}</a>

/**
 * One card per numbered section. The numbering is in the titles rather than generated,
 * so a section can be quoted by number in an email and still be found here later even
 * if another is inserted above it.
 */
const Section = ({ n, title, children }) => (
  <Card title={`${n}. ${title}`}>
    <div className="prose">{children}</div>
  </Card>
)

export default function Privacy() {
  return (
    <PageHeader title="Privacy Policy" sub={`Last updated ${LAST_UPDATED}`}>
      <div className="col">
        <Card>
          <div className="prose">
            <p>
              This policy explains how S4NArchive (the “archive”, “we”, “us”) collects,
              uses, and protects personal information. The archive is a private record of
              correspondence from field partners — updates, prayer requests, annual
              reports, photographs and the documents they came in — and it has no public
              pages: every account in it is created by invitation.
            </p>
            <p>
              The controller of this data is Trinity Applied Systems / SilkRoaders, PO Box
              41, Brentwood, TN 37024, United States. You can reach us at <Mail />.
            </p>
          </div>
        </Card>

        <Section n="1" title="Two kinds of people in here">
          <p>
            The distinction runs through the rest of this policy, so it comes first.
          </p>
          <ul>
            <li>
              <strong>People with accounts</strong> — staff and administrators, who sign
              in through Cloudflare Access at the main address, and donors, who sign in
              with an email address and password at the donor address. This is data you
              give us in order to use the archive.
            </li>
            <li>
              <strong>People the archive is about</strong> — the partners whose updates
              are held here, and anyone they wrote about. They did not create an account
              and mostly cannot see this page. Section 4 is about them.
            </li>
          </ul>
        </Section>

        <Section n="2" title="Information we collect about account holders">
          <ul>
            <li>
              <strong>Account data.</strong> Your email address, a display name, your role
              in the archive, and who invited you. For donor accounts we also store a
              password, held as a PBKDF2-SHA256 hash over 100,000 rounds with a random
              salt per account — never the password itself. Staff accounts have no password
              here at all: Cloudflare Access authenticates you before a request reaches us.
            </li>
            <li>
              <strong>Session records.</strong> For donor sessions we store a SHA-256 of
              the session token (not the token), when it expires, when it was last used,
              the IP address it was created from, and the browser’s user-agent string.
              Invitation and password-reset links are stored the same way — as hashes,
              single-use, and expiring.
            </li>
            <li>
              <strong>What you did.</strong> An audit record of actions that change the
              archive: your email address, what you did, to which record, a short summary,
              and the time. Your account also carries the time it was last active, which is
              how the ninety-minute idle timeout is enforced.
            </li>
            <li>
              <strong>Technical data.</strong> When something fails we record the message,
              the request path and status, and — for security and abuse handling — the IP
              address the request came from and the country and city Cloudflare derives
              from it. Cloudflare keeps its own request logs for the same purposes.
            </li>
            <li>
              <strong>AI usage.</strong> When an assisted feature is used we record which
              provider and model answered, which field it was for, how long it took, and
              how many characters went in and came out. We do not store the prompt or the
              generated text in that record.
            </li>
            <li>
              <strong>Preferences.</strong> Your theme, which sidebar groups you left
              folded, and similar display choices are kept in your browser’s local storage
              and are never sent to us. Clearing your browser data clears them.
            </li>
          </ul>
          <p>
            No payments are taken in the archive. A partner profile may link to that
            partner’s own giving page on another site; following such a link takes you to
            a service with its own privacy policy, and we receive nothing from it.
          </p>
        </Section>

        <Section n="3" title="How we use it, and our legal bases">
          <p>Under the EU/UK GDPR we rely on the following:</p>
          <ul>
            <li>
              <strong>To run the archive</strong> — keeping your account, serving the
              records you are entitled to see, and remembering your favorites:
              performance of a contract, and our legitimate interest in maintaining the
              archive.
            </li>
            <li>
              <strong>To know who you are and keep the door shut</strong> —
              authentication, rate limiting, the Turnstile challenge on the donor door, the
              idle timeout, and the audit record: legitimate interests, and contract.
            </li>
            <li>
              <strong>To send the two letters we send</strong> — a donor invitation and a
              password reset: contract, and at your request. We send no other email and
              run no mailing list.
            </li>
            <li>
              <strong>To keep the archive as a record</strong> of what partners reported
              and when: legitimate interests.
            </li>
            <li>
              <strong>To meet legal obligations</strong> and protect our rights and the
              safety of the people in the archive: legal obligation and legitimate
              interests.
            </li>
          </ul>
          <p>
            We do not advertise, we do not profile anyone for advertising, and we do not
            sell or rent personal data.
          </p>
        </Section>

        <Section n="4" title="Information about the people the archive is about">
          <p>
            Most of the personal information here was not given to us by the person it
            describes. It is the content of partner correspondence: names, the country and
            region someone works in, the work they report, prayer requests, photographs,
            and needs they have raised.
          </p>
          <p>
            Some of it is sensitive on its face — it concerns religious activity, and in
            several countries it concerns people for whom being identified carries real
            risk. That is the reason this application exists the way it does: there are no
            public pages, every account is created by hand, roles limit what an account can
            open, and the archive holds tools for redacting names and covering faces in the
            editions served to accounts that are given a view of the archive rather than
            the run of it.
          </p>
          <p>
            If you are one of the people written about here and you want to know what the
            archive holds about you, or want it corrected or removed, write to <Mail /> and
            we will act on it. You do not need an account to ask.
          </p>
        </Section>

        <Section n="5" title="Service providers">
          <p>
            We share data only with providers who process it on our behalf under contract:
          </p>
          <ul>
            <li>
              <strong>Cloudflare</strong> — hosting and content delivery (Workers), the
              database (D1), file and image storage (R2 and Images), staff authentication
              (Access / Zero Trust), the bot challenge on the donor door (Turnstile),
              outbound email for the two letters above (Email Sending), and the built-in
              models described in section 6 (Workers AI).
            </li>
            <li>
              <strong>An external model provider</strong>, only if an administrator has
              enabled one — see section 6.
            </li>
          </ul>
          <p>
            We may also disclose information where the law requires it, to enforce our
            terms, or to protect the rights and safety of our users and the people in the
            archive.
          </p>
        </Section>

        <Section n="6" title="Assisted features and models">
          <p>
            The archive can help with editorial work: reading the text out of a scanned
            newsletter, drafting a summary, describing a photograph, proposing redactions.
            By default that work is done by models running on Cloudflare’s network through
            the Workers AI binding, and the material does not leave that platform.
          </p>
          <p>
            An administrator may instead configure an external provider — OpenAI,
            Anthropic, or Google AI Studio. Every one of those is disabled until a key is
            installed. When one is in use, the text or image being worked on is sent to
            that provider in order to be processed, under their terms as a processor. No
            provider is given the archive as a whole, and none is given access to accounts,
            sessions, or logs.
          </p>
        </Section>

        <Section n="7" title="International data transfers">
          <p>
            We are based in the United States and our providers process data there and on
            globally distributed networks. Where personal data is transferred out of the
            EEA, the UK, or Switzerland, we rely on appropriate safeguards, including the
            European Commission’s Standard Contractual Clauses as incorporated in our
            providers’ terms.
          </p>
        </Section>

        <Section n="8" title="How long we keep things">
          <ul>
            <li>
              <strong>Archive content</strong> is kept indefinitely. Being a durable record
              of what partners reported is the purpose of the archive; material is removed
              when it is wrong, when it is unsafe to hold, or on request under section 4 or
              9.
            </li>
            <li>
              <strong>Accounts</strong> last until an administrator deactivates or deletes
              them. Deleting a donor account deletes its sessions and any outstanding
              invitation or reset links with it.
            </li>
            <li>
              <strong>Sessions</strong> end after ninety minutes of inactivity, and in any
              case six hours after signing in at the donor door. Reset links expire in an
              hour, invitations in seven days, and all of them are single-use.
            </li>
            <li>
              <strong>Audit records</strong> are kept for as long as the records they
              describe, because they are what makes the archive answerable for its own
              contents.
            </li>
            <li>
              <strong>Failure records and rate-limit counters</strong> are working data:
              counters are swept automatically, and failure records are cleared once they
              have been dealt with.
            </li>
          </ul>
        </Section>

        <Section n="9" title="Your rights in the EU and UK">
          <p>You have the right to:</p>
          <ul>
            <li>access the personal data we hold about you;</li>
            <li>have inaccurate data corrected;</li>
            <li>have your data erased;</li>
            <li>restrict or object to how we process it;</li>
            <li>receive a copy in a portable form;</li>
            <li>withdraw consent, where we relied on consent.</li>
          </ul>
          <p>
            There is no self-service delete: accounts here are created and removed by an
            administrator, which is also how a request is carried out. Write to <Mail /> and
            we will confirm what we hold, what we changed, and when. You also have the right
            to complain to your local data protection authority.
          </p>
        </Section>

        <Section n="10" title="Your rights in the United States">
          <p>
            Depending on your state — California under the CCPA/CPRA, and states with
            comparable laws — you may have the right to know what personal information we
            collect and why, to access, correct, or delete it, and to opt out of the “sale”
            or “sharing” of personal information. We do not sell or share personal
            information, and we will not treat you differently for exercising a right. To
            make a request, write to <Mail />.
          </p>
          <p>
            The categories we collect are in sections 2 and 4, the purposes in section 3,
            and the parties we disclose to in sections 5 and 6.
          </p>
        </Section>

        <Section n="11" title="Do Not Track">
          <p>
            We do not track anybody across other websites, and there are no advertising or
            analytics trackers in this application. We do not respond to browser “Do Not
            Track” signals, because there is no agreed standard for what a response would
            mean. We process only what this policy describes.
          </p>
        </Section>

        <Section n="12" title="Children">
          <p>
            The archive is not directed to children under 16 and accounts are not issued to
            them. Partner correspondence may mention children — families, schools, and the
            work being reported. If you believe we hold information about a child that
            should not be here, write to <Mail /> and we will remove it.
          </p>
        </Section>

        <Section n="13" title="Security">
          <p>
            Everything is served over HTTPS. The main address sits behind Cloudflare Access,
            so an unauthenticated request never reaches the application. The donor door has
            its own controls: a bot challenge, per-address and per-IP rate limits, lockout
            after repeated failures, a minimum password length, hashed passwords, and
            session tokens that are stored only as hashes so a copy of the database contains
            no session anybody can use. Both doors forget you after ninety minutes of
            inactivity. What an account can read and write is limited by its role, and
            changes are recorded.
          </p>
          <p>
            No system is completely secure. If a breach affects your personal data we will
            notify you and the relevant authorities where the law requires it.
          </p>
        </Section>

        <Section n="14" title="Changes to this policy">
          <p>
            We may update this policy. The date under the title above is the date of the
            current version, and for a material change we will say so inside the
            application rather than only changing the date.
          </p>
        </Section>

        <Section n="15" title="Contact">
          <p>
            Questions and requests: <Mail />.
          </p>
          <p>
            Trinity Applied Systems / SilkRoaders, PO Box 41, Brentwood, TN 37024, United
            States.
          </p>
        </Section>
      </div>
    </PageHeader>
  )
}
